import { useCallback } from 'react';

import { generatePath, useNavigate, useParams } from 'react-router-dom';

import * as ROUTES from '#client/constants/routes';

/**
 * Hook for handling thread navigation
 *
 * Features:
 * - Navigate between threads with minimal rerendering
 * - Proper URL management
 * - Loading states during navigation
 * - Current thread detection
 *
 * @returns {object} Navigation functions and current thread info
 */
export function useThreadNavigation() {
  const navigate = useNavigate();
  const { threadUuid: currentThreadUuid } = useParams();

  /**
   * Navigate to a specific thread
   * @param {string} threadUuid - UUID of the thread to navigate to
   * @param {object} options - Navigation options
   */
  const navigateToThread = useCallback(
    (threadUuid, options = {}) => {
      if (!threadUuid) {
        console.warn('[useThreadNavigation] No threadUuid provided for navigation');
        return;
      }

      if (threadUuid === currentThreadUuid) {
        return;
      }

      const { replace = false, state = {} } = options;

      navigate(generatePath(ROUTES.thread, { threadUuid }), {
        replace,
        state: {
          fromSidebar: true,
          previousThreadUuid: currentThreadUuid,
          ...state
        }
      });
    },
    [navigate, currentThreadUuid]
  );

  /**
   * Navigate to thread creator (new thread)
   * @param {string} businessEntityUuid - Business entity UUID
   * @param {object} options - Navigation options
   */
  const navigateToNewThread = useCallback(
    (businessEntityUuid, options = {}) => {
      if (!businessEntityUuid) {
        console.warn(
          '[useThreadNavigation] No businessEntityUuid provided for new thread creation'
        );
        return;
      }

      const { replace = false, state = {} } = options;

      navigate(generatePath(ROUTES.threadCreator, { businessEntityUuid }), {
        replace,
        state: {
          fromSidebar: true,
          previousThreadUuid: currentThreadUuid,
          ...state
        }
      });
    },
    [navigate, currentThreadUuid]
  );

  /**
   * Navigate to thread creator (new thread)
   * @param {string} visitUuid - Visit UUID
   * @param {object} options - Navigation options
   */
  const navigateToNewVisitorThread = useCallback(
    (options = {}) => {
      const { replace = false, state = {} } = options;

      navigate(ROUTES.visitorThreadCreator, {
        replace,
        state: {
          fromSidebar: true,
          previousThreadUuid: currentThreadUuid,
          ...state
        }
      });
    },
    [navigate, currentThreadUuid]
  );

  /**
   * Check if a thread is currently active
   * @param {string} threadUuid - Thread UUID to check
   * @returns {boolean} True if the thread is currently active
   */
  const isThreadActive = useCallback(
    (threadUuid) => {
      return threadUuid === currentThreadUuid;
    },
    [currentThreadUuid]
  );

  /**
   * Get thread URL for a given thread UUID
   * @param {string} threadUuid - Thread UUID
   * @returns {string} Full thread URL
   */
  const getThreadUrl = useCallback((threadUuid) => {
    return `/guide/thread/${threadUuid}`;
  }, []);

  /**
   * Get new thread URL for a business entity
   * @param {string} businessEntityUuid - Business entity UUID
   * @returns {string} New thread creation URL
   */
  const getNewThreadUrl = useCallback((businessEntityUuid) => {
    return `/guide/company/${businessEntityUuid}`;
  }, []);

  return {
    // Current state
    currentThreadUuid,

    // Navigation functions
    navigateToThread,
    navigateToNewThread,
    navigateToNewVisitorThread,

    // Utility functions
    isThreadActive,
    getThreadUrl,
    getNewThreadUrl
  };
}
